import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Shield, ShieldOff } from "lucide-react";

import BaseDropdown, { adaptInfiniteLoader } from "@reusables/dropdowns/BaseDropdown";
import BaseInfiniteLoader from "@reusables/dropdowns/BaseInfiniteLoader";
import BaseDatepicker from "@reusables/BaseDatepickerLegacy";
import { DatepickerRangeContext } from "@reusables/BaseDatepickerLegacy/context";
import BaseInputsGrid from "@reusables/BaseInputsGrid";

import { useGetModuleCustomersQuery } from "@redux/features/customers/customersApi";
import { productsApi } from "@redux/features/products/productsApi";
import { isErrorWithMessage } from "@redux/api/query";
import { useAppDispatch } from "@redux/hooks";

import { genT, PredefinedTranslations, removeEmpty, useDatesRangeFilter } from "@helpers/utils";

import { BadgeStatus, Product, SaleOrder } from "@/types/general";
import { statusOptions } from "@components/Dashboard/pages/Sales/Orders/utils";
import { toast } from "react-toastify";
import i18n from "@/i18n";

interface UseOrdersFiltersProps {
    onFiltersChange: (filters: SaleOrder.DTO.Filters) => void;
    onSearchChange: (value: string) => void;
}

export default function useOrdersFilters({ onFiltersChange, onSearchChange }: UseOrdersFiltersProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.main" });
    const dispatch = useAppDispatch();

    // Customers filter
    const { data: customersOptions, isLoading: customersOptionsLoading } = useGetModuleCustomersQuery("sale");
    const [selectedCustomers, setSelectedCustomers] = useState<typeof customersOptions>([]);

    // Products filter (lazy loading)
    const [productsOptions, setProductsOptions] = useState<Product.Slim[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<Product.Slim[]>();
    const [isProductLazyLoadingFetching, setIsProductLazyLoadingFetching] = useState(false);

    // Status filter
    const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: BadgeStatus.Base; }[]>([]);

    // Date range filter
    const { datesRange, setDatesRange, filterAdaptedDatesRange } = useDatesRangeFilter();

    // Reservations presence filter
    const reservationsOptions = [
        { id: 0, value: t("filters.reservations.options.no_reservations") }, // No reservations
        { id: 1, value: t("filters.reservations.options.has_reservations") }  // Has reservations
    ];
    const [selectedReservations, setSelectedReservations] = useState<typeof reservationsOptions>([]);

    // Building and emitting filters whenever any filter value changes
    useEffect(() => {
        const filters: SaleOrder.DTO.Filters = removeEmpty({
            customers: selectedCustomers?.map(x => x.id),
            products: selectedProducts?.map(x => x.id),
            dates_range: filterAdaptedDatesRange,
            status: selectedStatus?.map(x => x.value),
            has_reservations: selectedReservations.length 
                ? selectedReservations[0].id === 1 
                : undefined
        });

        onFiltersChange(filters);
    }, [
        selectedCustomers,
        selectedProducts,
        filterAdaptedDatesRange,
        selectedStatus,
        selectedReservations,
        onFiltersChange
    ]);

    // Searching debounce handler
    const searchInputDebounce = useCallback(
        _.debounce((value: string) => onSearchChange(value), 1000),
        [onSearchChange]
    );

    return {
        searchHandler: searchInputDebounce,
        filterItems: (
            <div className="w-[702px]">
                <BaseInputsGrid cols={2} gap={24}>
                    <BaseDropdown
                        label={t("filters.customer.label")}
                        options={customersOptions}
                        value={selectedCustomers}
                        getter={{
                            label: (item) => item.name,
                            key: (item) => item.id
                        }}
                        multiple
                        autocomplete
                        brightLabel
                        onChange={(_, opts) => setSelectedCustomers(opts)}
                        emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                        isLoading={customersOptionsLoading}
                    />

                    <BaseInfiniteLoader
                        fetch={(search, page, limit) => {
                            setIsProductLazyLoadingFetching(true);
                            dispatch(productsApi.endpoints.getModuleProductsFull.initiate(
                                {
                                    filters: {
                                        search,
                                    },
                                    pagination: {
                                        page,
                                        limit
                                    },
                                    module: "sale"
                                },
                                {
                                    subscribe: false
                                }
                            )).unwrap()
                                .then(res => {
                                    setProductsOptions(res?.payload ?? []);
                                })
                                .catch(e => {
                                    if (isErrorWithMessage(e)) {
                                        toast.error(e.message);
                                    } else {
                                        toast.error(i18n.t("general.responses.somethingWentWrong"));
                                    }
                                })
                                .finally(() => {
                                    setIsProductLazyLoadingFetching(false);
                                });
                        }}
                        limit={100}
                        result={productsOptions}
                        isLoading={isProductLazyLoadingFetching}
                    >
                        {(infinity) => (
                            <BaseDropdown
                                {...adaptInfiniteLoader(infinity)}
                                label={t("filters.product.label")}
                                value={selectedProducts}
                                getter={{
                                    label: opt => opt.name,
                                    key: opt => opt.id,
                                    renderOption: (opt, icon) => (
                                        <div>
                                            <span className="grow">{opt.name}</span>
                                            {
                                                selectedProducts?.includes(opt) ? icon :
                                                    <span className="text-lightGreen-500 font-thin">{opt.code}</span>
                                            }
                                        </div>
                                    )
                                }}
                                onChange={(_, opts) => setSelectedProducts(opts)}
                                virtualize
                                autocomplete
                                multiple
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                brightLabel
                            />
                        )}
                    </BaseInfiniteLoader>

                    <DatepickerRangeContext.Provider value={{
                        "delivery_date": { range: datesRange, setRange: setDatesRange }
                    }}>
                        <BaseDatepicker
                            label={t("filters.date.label")}
                            placeholder={t("filters.date.placeholder")}
                            rangeConfig={{ groupKey: "delivery_date", role: "solo" }}
                            brightLabel
                        />
                    </DatepickerRangeContext.Provider>

                    <BaseDropdown
                        label={t("filters.status.label")}
                        options={statusOptions}
                        getter={{
                            label: (item) => item.label,
                            key: (item) => item.value
                        }}
                        onChange={(_, opts) => setSelectedStatus(opts)}
                        value={selectedStatus}
                        emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                    />

                    <BaseDropdown
                        label={t("filters.reservations.label")}
                        options={reservationsOptions}
                        value={selectedReservations}
                        getter={{
                            label: (opt) => opt.value,
                            key: (opt) => opt.id,
                            renderOption: (opt, icon) => (
                                <div className="flex items-center gap-2">
                                    {opt.id === 1 ? (
                                        <Shield size={14} className="text-[#A6DD4C]" />
                                    ) : (
                                        <ShieldOff size={14} className="text-[#686868]" />
                                    )}
                                    <span>{opt.value}</span>
                                    {icon}
                                </div>
                            )
                        }}
                        onChange={(_, opts) => setSelectedReservations(opts)}
                        emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                        brightLabel
                    />
                </BaseInputsGrid>
            </div>
        )
    };
}

